<template>
    <div :class="namespaceClass('__content')">
        <div :class="[namespaceClass('-chart-area')]">
            <div :class="namespaceClass('-chart-area-overlay')">
                <div :class="namespaceClass('-growth')">
                    <mds-section :title="this.getUILabel('chart_performance_market_value_title')" border="secondary" borderPosition="top" :size="6">
                    </mds-section>
                    <hypo-growth-chart
                        :key="marketChart"
                        :mwcId.prop="`${$router.app.mwcId}.performance_market_value`"
                        :model-data.prop="$store.getters['chart/growthChartData']"
                        :colors.prop="growthChartColors"
                        :dotted-lines.prop="growthChartDottedLines"
                        :currencySymbol.prop="$store.getters['hypo/hypoCurrencySymbol']"
                    ></hypo-growth-chart>
                    <hypo-total-period-table :componentData="totalPeriodTableData" mwc-id="hypoTotalPeriodTable" />
                </div>
                <div :class="namespaceClass('-trailing-return')">
                    <mds-section :title="this.getUILabel('chart_performance_trailing_return_title')" border="secondary" borderPosition="top" :size="6">
                    </mds-section>
                    <hypo-return-bar-chart
                        :mwcId.prop="`${$router.app.mwcId}.performance_trailing_return`"
                        key="performance_trailing_return"
                        :model-data.prop="trailingReturnData"
                        :config.prop="trailingReturnConfig"
                        :colors.prop="trailingReturnChartColors"
                    ></hypo-return-bar-chart>
                </div>
                <div :class="namespaceClass('-annual-return')">
                    <mds-section :title="this.getUILabel('chart_performance_annual_return_title')" border="secondary" borderPosition="top" :size="6">
                    </mds-section>
                    <hypo-return-bar-chart
                        :mwcId.prop="`${$router.app.mwcId}.performance_annual_return`"
                        key="performance_annual_return"
                        :model-data.prop="$store.getters['chart/annualReturnData']"
                        :colors.prop="annualReturnChartColors"
                        :config.prop="{}"
                    ></hypo-return-bar-chart>
                </div>
                <div :class="namespaceClass('-broad-asset')">
                    <mds-section border="secondary" borderPosition="top" :size="6">
                        <template v-slot:mds-section-title>
                            {{ getUILabel("chart_asset_allocation") }}
                            <span class="time-label"> {{ getUILabel("as_of") }} {{ $store.getters["chart/assetAllocationReleaseDate"] }}</span>
                        </template>
                    </mds-section>
                    <mds-layout-grid gutters="none" v-if="!isInvestmentFlow">
                        <mds-row class="asset-allocation">
                            <mds-col v-for="(portfolio, key, index) in assetAllocationData" :key="key" :cols="getCols">
                                <template v-if="isValidAssetAllocation(portfolio)">
                                    <hypo-asset-allocation-horizontal-bar-chart
                                        mwc-id="assetAllocationHorizontalChart"
                                        :key="marketChart"
                                        :index="index"
                                        :model-data.prop="portfolio"
                                        :boundaries.prop="chartBoundaries"
                                        :isSinglePortfolio.prop="isSinglePortfolio"
                                    />
                                </template>
                            </mds-col>
                        </mds-row>
                    </mds-layout-grid>
                </div>
                <div :class="namespaceClass('-detailed-asset')">
                    <hypo-allocation-table :componentData="detailAssetTableData" mwc-id="hypoAllocationTable" />
                </div>
                <div :class="{ 'holdings-container': portfolioList.length == 1 }" v-if="!isInvestmentFlow">
                    <mds-section style="margin-bottom: 16px;" border="secondary" borderPosition="top" :size="6">
                        {{ getUILabel("holdings_section") }}
                        <span class="time-label"> {{ getUILabel("as_of") }} {{ $store.getters["chart/holdingsDate"] }}</span>
                    </mds-section>
                    <hypo-holdings mwc-id="hypoHoldings" :componentData="holdingsComponentData" />
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import "hypo-growth-chart/src/index";
import "hypo-return-bar-chart/src/index";
import HypoVueBaseMixin from "hypo-vue-base-mixin";
import MwcSharedMixin from "@/mixins/MwcSharedMixin";
import HypoAllocationTable from "hypo-allocation-table/src/AllocationTable";
import HypoTotalPeriodTable from "hypo-total-period-table/src/TotalPeriodTable";
import { MdsLayoutGrid, MdsRow, MdsCol } from "@mds/layout-grid";
import "hypo-asset-allocation-horizontal-bar-chart/src";
import HypoHoldings from "hypo-holdings/src/Holdings.vue";
import { getEdgeValues } from "hypo-asset-allocation-horizontal-bar-chart/src/utils/assetAllocationUtils";
import MdsSection from "@mds/section";
export default {
    name: "investments-charts",
    mixins: [HypoVueBaseMixin, MwcSharedMixin],
    components: {
        HypoAllocationTable,
        HypoTotalPeriodTable,
        MdsLayoutGrid,
        MdsRow,
        MdsCol,
        MdsSection,
        HypoHoldings
    },
    data() {
        return {
            marketChartTimestamp: Date.now(),
            defaultConfig: {
                labels: this.$store.state.labels.labels
            },
            trailingReturnTimeLabelsMapping: {
                1: "trailing_return_xdomain_1_month",
                3: "trailing_return_xdomain_3_month",
                6: "trailing_return_xdomain_6_month",
                12: "trailing_return_xdomain_1_year",
                24: "trailing_return_xdomain_2_year",
                36: "trailing_return_xdomain_3_year",
                60: "trailing_return_xdomain_5_year",
                120: "trailing_return_xdomain_10_year"
            }
        };
    },
    beforeUpdate() {
        this.marketChartTimestamp = Date.now();
    },
    computed: {
        isInvestmentFlow() {
            return parseInt(this.$store.getters["hypo/getType"]) === 2;
        },
        marketChart() {
            return this.marketChartTimestamp;
        },
        growthChartConfig() {
            return {
                settings: {
                    lineChartSettings: {
                        dottedLines: this.growthChartDottedLines
                    }
                }
            };
        },
        totalPeriodTableData() {
            let annualizedData = [];
            const chartInvestments = this.$store.getters["chart/investments"];
            if (this.isInvestmentFlow) {
                // TO DO: Once PCT locks the 5 holding limit, this code won't be necessary
                const holdingData = chartInvestments.filter(item => item.type === "Holding");
                const noHolding = chartInvestments.filter(item => item.type !== "Holding");
                holdingData.length = holdingData.length > 5 ? 5 : holdingData.length;
                annualizedData = holdingData.concat(noHolding);
            } else {
                annualizedData = chartInvestments;
            }

            const componentData = { rows: [{}, {}], columns: [], isInvestmentFlow: this.isInvestmentFlow };
            const properties = [];
            if (annualizedData) {
                annualizedData.map((item, index) => {
                    if (this.isInvestmentFlow && item.type === "Holding") {
                        properties.push(item.name);
                    } else if (this.isInvestmentFlow && item.type === "Benchmark") {
                        properties.push("Benchmark");
                    } else {
                        properties.push(item.type.toLowerCase() + index);
                    }
                    componentData.rows[0][properties[index]] = item.annualizedTotalReturn;
                    componentData.rows[1][properties[index]] = item.cumulativeReturn;
                    componentData.columns[index] = { value: properties[index] };
                });
                componentData.columns.unshift({ value: "name" });
                componentData.rows[0]["name"] = "Annualized";
                componentData.rows[1]["name"] = "Cumulative";
                componentData.rows[0]["id"] = "annulizedReturn";
                componentData.rows[1]["id"] = "cumulativeReturn";
            }
            return componentData;
        },
        growthChartColors() {
            return this.$store.getters["chart/growthChartData"].map(item => item.color);
        },
        growthChartDottedLines() {
            return this.$store.getters["chart/growthChartDottedLines"];
        },
        trailingReturnData() {
            return this.$store.getters["chart/trailingReturnData"].map(item => {
                return {
                    ...item,
                    data: item.data.map(i => {
                        return {
                            value: i.value,
                            timePeriod: this.getUILabel(this.trailingReturnTimeLabelsMapping[i.timePeriod])
                        };
                    })
                };
            });
        },
        trailingReturnConfig() {
            return {
                settings: {
                    emptyChartSetting: {
                        xDomain: [
                            this.getUILabel("trailing_return_xdomain_3_month"),
                            this.getUILabel("trailing_return_xdomain_1_year"),
                            this.getUILabel("trailing_return_xdomain_3_year"),
                            this.getUILabel("trailing_return_xdomain_5_year"),
                            this.getUILabel("trailing_return_xdomain_10_year")
                        ]
                    }
                }
            };
        },
        trailingReturnChartColors() {
            return this.$store.getters["chart/trailingReturnData"].map(item => item.color);
        },
        annualReturnChartColors() {
            return this.$store.getters["chart/annualReturnData"].map(item => item.color);
        },
        numberFormatter() {
            const self = this;
            return function(value) {
                return self.formatNumberSymbol(self.formatter.number(value, "shortDecimal"));
            };
        },
        detailAssetTableData() {
            const detailAssetClassData = this.$store.getters["chart/detailAssetClassData"];
            const rows = detailAssetClassData.rows.map(row => {
                return detailAssetClassData.columns
                    .filter((col, index) => index !== 0)
                    .reduce((prev, col, index) => {
                        let formattedValue = prev[`${index}`];
                        if (typeof formattedValue === "number") {
                            formattedValue = this.numberFormatter(formattedValue);
                        }
                        return {
                            ...prev,
                            [`${index}`]: formattedValue
                        };
                    }, row);
            });
            return {
                ...detailAssetClassData,
                rows
            };
        },
        assetAllocationData() {
            return this.$store.getters["chart/assetAllocationBarData"];
        },
        chartBoundaries() {
            const tableLimit = 100;
            const numIntervals = 4;
            const chartData = this.assetAllocationData;
            if (Object.keys(this.assetAllocationData).length === 0) {
                return {
                    minXValue: 0,
                    maxXValue: 0,
                    xIntervals: 0
                };
            }
            const egdeValues = [];
            Object.values(chartData).forEach(item => {
                if (this.isValidAssetAllocation(item)) {
                    egdeValues.push(getEdgeValues(item));
                }
            });
            let maxVal = 0;
            let minVal = 0;
            egdeValues.forEach(item => {
                maxVal = item.max > maxVal ? item.max : maxVal;
                minVal = item.min < minVal ? item.min : minVal;
            });
            const baseInterval = Math.floor(minVal < 0 ? numIntervals / 2 : numIntervals);
            // minVal is either 0 or a negativbe number, so -minVal = Math.abs(minVal)
            let tableMax = Math.max(maxVal, -minVal);
            // If not "maxVal + 1", data could overflow due to Intervals rounding
            tableMax = tableMax % 10 !== 0 ? Math.ceil(tableMax / 10) * 10 : Math.ceil((tableMax + 1) / 10) * 10;
            const flag = minVal >= 0 && tableMax > tableLimit;
            tableMax = flag ? tableLimit : tableMax;
            const tableMin = minVal < 0 ? -tableMax : 0;

            return {
                minXValue: tableMin,
                maxXValue: tableMax,
                xIntervals: Math.floor(tableMax / baseInterval)
            };
        },
        getCols() {
            return 12 / (Object.keys(this.assetAllocationData).length || 1);
        },
        holdingsComponentData() {
            const portfolioList = this.$store.getters["chart/holdingsTableData"].map((portfolio, index) => {
                const securities = portfolio.holdings
                    ? portfolio.holdings.map(security => {
                          return { ...security, id: security.securityId };
                      })
                    : [];
                return { ...portfolio, securities: securities };
            });
            return { portfolioList };
        },
        portfolioList() {
            return this.$store.getters["chart/holdingsTableData"];
        },
        isSinglePortfolio() {
            return parseInt(this.$store.getters["hypo/getType"]) === 0;
        }
    },
    methods: {
        isValidAssetAllocation(portfolioAssetAllocationData) {
            return Object.keys(portfolioAssetAllocationData).length > 0;
        }
    }
};
</script>

<style lang="scss" scoped>
.time-label {
    color: #5e5e5e;
    font-size: 14px;
    line-height: 19px;
    font-weight: normal;
}
.investments-charts {
    &-chart-area {
        position: relative;
    }

    &-growth {
        ::v-deep .hypo-growth-chart__line-chart {
            overflow: hidden;
            height: 389px;
        }
    }

    &-growth,
    &-trailing-return,
    &-annual-return,
    &-broad-asset,
    &-detailed-asset {
        margin-bottom: $mds-space-4-x;
        ::v-deep .mds-header {
            padding-top: 5px;
            margin-bottom: $mds-space-2-x;
            display: flex;
        }
        .asset-allocation {
            min-height: 189px;
            ::v-deep .mbc-horizontal-bar-chart {
                min-height: 160px;
            }
        }
    }
}
</style>
